import { Link } from '@remix-run/react'
import { Menu as MenuIcon, X as CloseIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import logoUrl from '#app/routes/_marketing+/logos/momentumlogo.png'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

const NAV_LINKS = [
	{ to: '/about', label: 'About' },
	{ to: '/how-to', label: 'How-to' },
	{ to: '/reference', label: 'Reference' },
	{ to: '/coach', label: 'For Coaches' },
] as const

export function MarketingHeader({
	isAuthenticated,
	signupEnabled,
}: {
	isAuthenticated: boolean
	signupEnabled: boolean
}) {
	const [scrolled, setScrolled] = useState(false)
	const [mobileOpen, setMobileOpen] = useState(false)
	const sentinelRef = useRef<HTMLDivElement>(null)
	const user = useOptionalUser()

	useEffect(() => {
		const sentinel = sentinelRef.current
		if (!sentinel) return
		const observer = new IntersectionObserver(([entry]) =>
			setScrolled(!entry?.isIntersecting),
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (!mobileOpen) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setMobileOpen(false)
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [mobileOpen])

	return (
		<>
			<div
				ref={sentinelRef}
				aria-hidden
				className="absolute top-0 h-px w-full"
			/>
			<header
				className={[
					'sticky top-0 z-40 transition-colors duration-200',
					scrolled
						? 'bg-ink/95 border-b border-m-rule backdrop-blur-sm'
						: 'bg-transparent',
				].join(' ')}
			>
				<div className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-4 md:px-10">
					<Link
						to="/"
						className="flex items-center gap-2"
						aria-label="Momentum home"
					>
						<img src={logoUrl} alt="" className="h-8 w-8" />
						<span className="font-display text-lg font-semibold tracking-tight">
							Momentum
						</span>
					</Link>

					<nav className="hidden items-center gap-8 md:flex">
						{NAV_LINKS.map((link) => (
							<Link
								key={link.to}
								to={link.to}
								className="text-sm text-ink-fg-soft transition-colors hover:text-ink-fg"
							>
								{link.label}
							</Link>
						))}
						{isAuthenticated && user ? (
							<Link
								to="/dashboard"
								aria-label="Open dashboard"
								className="ml-2"
							>
								<img
									src={getUserImgSrc(user.image?.id)}
									alt=""
									className="h-9 w-9 rounded-full border border-m-rule"
								/>
							</Link>
						) : (
							<>
								<Link
									to="/login"
									className="text-sm text-ink-fg-soft transition-colors hover:text-ink-fg"
								>
									Log in
								</Link>
								{signupEnabled ? (
									<Link
										to="/signup"
										className="rounded-md bg-ember px-4 py-2 text-sm font-medium text-ink transition-opacity hover:opacity-90"
									>
										Sign up
									</Link>
								) : null}
							</>
						)}
					</nav>

					<button
						type="button"
						className="md:hidden"
						aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
						aria-expanded={mobileOpen}
						aria-controls="marketing-mobile-nav"
						onClick={() => setMobileOpen((o) => !o)}
					>
						{mobileOpen ? <CloseIcon /> : <MenuIcon />}
					</button>
				</div>

				{mobileOpen ? (
					<div
						id="marketing-mobile-nav"
						className="border-t border-m-rule bg-ink md:hidden"
					>
						<nav className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 py-6">
							{NAV_LINKS.map((link) => (
								<Link
									key={link.to}
									to={link.to}
									className="text-base text-ink-fg-soft hover:text-ink-fg"
									onClick={() => setMobileOpen(false)}
								>
									{link.label}
								</Link>
							))}
							{isAuthenticated && user ? (
								<Link
									to="/dashboard"
									className="text-base text-ember"
									onClick={() => setMobileOpen(false)}
								>
									Dashboard →
								</Link>
							) : (
								<>
									<Link
										to="/login"
										className="text-base text-ink-fg-soft hover:text-ink-fg"
										onClick={() => setMobileOpen(false)}
									>
										Log in
									</Link>
									{signupEnabled ? (
										<Link
											to="/signup"
											className="inline-block w-fit rounded-md bg-ember px-4 py-2 text-sm font-medium text-ink"
											onClick={() => setMobileOpen(false)}
										>
											Sign up
										</Link>
									) : null}
								</>
							)}
						</nav>
					</div>
				) : null}
			</header>
		</>
	)
}
